import { pdfjs } from 'react-pdf';
import { CSV_BOM, CSV_LINE_BREAK } from './constants';
import { logger } from './logger';
import { BigCalculator } from './number-calculator';

export const EMPTY_DATA_ERROR = 'empty-data';
export type PaperSize = 'A4' | 'A3';

// Note: Required to set workerSrc otherwise it throws an error like Error: No "GlobalWorkerOptions.workerSrc" specified
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const FILE_EXTENSION = 4; // '.csv' '.pdf'

// Note:
// ファイル名に使えない文字は ブラウザの仕様でデフォルトで置換されるため、
// この関数では現状「.」のみを置換する
const replaceUnusableCharacterForFileName = (fileName: string) =>
  fileName.replaceAll('.', '_');

export const substrFileName = ({
  name,
  fileType,
  maxFilenameLength = 200,
}: {
  name: string;
  fileType?: 'pdf' | 'csv';
  maxFilenameLength?: number;
}) => {
  const subtractedName = name.substring(
    0,
    BigCalculator.subtract(maxFilenameLength, fileType ? FILE_EXTENSION : 0),
  );

  const fileName = replaceUnusableCharacterForFileName(subtractedName);

  return fileType ? `${fileName}.${fileType}` : fileName;
};

export const downloadURL = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const objectURL = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = objectURL;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectURL);
  } catch (error) {
    logger.error('Error downloading the file:', error);
  }
};

export const downloadFile = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.download = fileName;
  a.href = url;
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const downloadCsv = (csvData: string, fileName: string) => {
  const blob =
    csvData === ''
      ? new Blob([csvData], {
          type: 'text/csv',
        })
      : new Blob([CSV_BOM, csvData], {
          type: 'text/csv',
        });
  downloadFile(blob, fileName);
};

export const escapeForCSV = ({
  value,
  hasEnclosingCharacter = true,
}: {
  value: string | number | null | undefined;
  hasEnclosingCharacter?: boolean;
}) => {
  if (typeof value === 'string') {
    return hasEnclosingCharacter
      ? `"${value.replace(/"/g, '""')}"`
      : value.replace(/"/g, '""');
  }
  return hasEnclosingCharacter ? `"${value ?? ''}"` : (value ?? '');
};

export const createCsv = <T extends string>({
  headerKeys,
  headerConverter,
  body,
}: {
  headerKeys: T[];
  headerConverter: Record<T, string>;
  body: Array<Record<T, string | number | null | undefined>>;
}) => {
  const str = `${headerKeys
    .map((value) => {
      const headerTitle: string =
        value in headerConverter ? headerConverter[value] : value;

      return `"${headerTitle.replace(/\n/g, '')}"`;
    })
    .join(',')}${CSV_LINE_BREAK}`;

  return body.reduce((acc, report) => {
    const row = `${headerKeys
      .map((value) => escapeForCSV({ value: report[value] }))
      .join(',')}${CSV_LINE_BREAK}`;
    return acc + row;
  }, str);
};

export const getPdfPages = async (arrayBuffer: ArrayBuffer) => {
  const pdfData = await pdfjs.getDocument(arrayBuffer).promise;
  return pdfData.numPages;
};
